import {
	createContext,
	useCallback,
	useContext,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { useLocale } from './LocaleContext';
import {
	getUserInfo,
	singInByGoogle,
	singInRequest,
	singnOutRequest,
} from '../modules/SignIn/api';
import { updateProfile } from '../modules/Profile/api';
import { Language } from '../shared/consts/locale';

/** Map API language_preference to LocaleContext codes (en / hy / ru). */
const localeFromLanguagePreference = pref => {
	const p = (pref || 'en').toLowerCase();
	if (p === 'hy') return Language.Hy;
	if (p === 'ru') return Language.Ru;
	return Language.En;
};

export const AuthContext = createContext({
	isLoading: false,
	isAuth: false,
	googleAuth: userData => {},
	onLogin: async userData => {},
	onLogout: async () => {},
	fetchMe: async () => {},
});

export const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
	const { setErrorModalShow, handleSetLocale } = useLocale();
	const [isAuth, setIsAuth] = useState(
		!!window.localStorage.getItem('accessToken'),
	);
	const [isLoading, setIsLoading] = useState(false);
	const [userInfo, setUserInfo] = useState(null);

	const login = useCallback(
		async userData => {
			try {
				setIsLoading(true);
				const data = await singInRequest(userData, setErrorModalShow);
				const { access_token } = data;

				if (access_token) {
					window.localStorage.setItem('accessToken', access_token);
					window.localStorage.setItem('authProvider', 'email');
					setIsAuth(true);
					return data;
				}
			} catch (error) {
				setIsAuth(false);
				throw error.response?.data || error;
			} finally {
				setIsLoading(false);
			}
		},
		[setErrorModalShow],
	);

	const googleAuth = async userData => {
		try {
			setIsLoading(true);
			const response = await singInByGoogle(userData);

			// Check if the result is an error
			if (response && response.response) {
				// It's an error object
				const errorData = response.response?.data?.detail;
				throw new Error(errorData || 'Google authentication failed');
			}

			// Check if MFA is required
			if (response && response.requires_mfa) {
				// Return MFA data so the component can handle it
				return {
					requires_mfa: true,
					mfa_type: response.mfa_type,
					temp_token: response.temp_token,
					user: response.user,
				};
			}

			// Normal login success
			if (response && response.access_token) {
				window.localStorage.setItem('accessToken', response.access_token);
				window.localStorage.setItem('authProvider', 'google');
				setIsAuth(true);
				return response.access_token;
			} else {
				throw new Error('No token received from Google authentication');
			}
		} catch (error) {
			console.error('Google auth error:', error);
			setIsAuth(false);
			throw error.response?.data || error;
		} finally {
			setIsLoading(false);
		}
	};

	const fetchMe = useCallback(async () => {
		try {
			setIsLoading(true);
			const data = await getUserInfo(setErrorModalShow);

			if (data) {
				const localLanguage = window.localStorage.getItem('language');
				let effectiveLanguage = data.language_preference || Language.En;

				// Keep profile language in sync with language picked on public pages.
				if (
					localLanguage &&
					Object.values(Language).includes(localLanguage) &&
					localLanguage !== effectiveLanguage
				) {
					const updateResult = await updateProfile({
						language_preference: localLanguage,
					});

					if (updateResult?.status === 200) {
						effectiveLanguage = localLanguage;
						data.language_preference = localLanguage;
					}
				}
				// console.log("User info:", data);

				setUserInfo(data);
				setIsAuth(true);
				handleSetLocale(localeFromLanguagePreference(effectiveLanguage));
				return data;
			} else {
				setIsAuth(false);
				window.localStorage.removeItem('accessToken');
			}
		} catch (error) {
			console.error('Fetch user info error:', error);
			setIsAuth(false);
			setUserInfo(null);
			window.localStorage.removeItem('accessToken');
			throw error;
		} finally {
			setIsLoading(false);
		}
	}, [handleSetLocale, setErrorModalShow]);

	const logout = useCallback(async () => {
		try {
			setIsLoading(true);
			await singnOutRequest();
		} catch (error) {
			console.error('Logout error:', error);
		} finally {
			setUserInfo(null);
			setIsAuth(false);
			window.localStorage.removeItem('accessToken');
			window.localStorage.removeItem('mfaSessionToken');
			window.localStorage.removeItem('authProvider');
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		const token = window.localStorage.getItem('accessToken');
		if (token) {
			setIsAuth(true);
		}
	}, []);

	const value = useMemo(
		() => ({
			isLoading,
			isAuth,
			setIsAuth,
			onLogin: login,
			onLogout: logout,
			userInfo,
			fetchMe,
			googleAuth,
		}),
		[
			isLoading,
			isAuth,
			setIsAuth,
			login,
			logout,
			userInfo,
			fetchMe,
			googleAuth,
		],
	);

	return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthProvider;
